import {
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  MenuItem,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AccordionActions,
} from '@mui/material';
import { RHFSelect, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/iconify';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useJobCreationContext } from 'src/sections/job2/hooks/use-job-creation-context';

export default function Screening({
  errors,
  watch,
  jobSkills,
  isScreeningExpanded,
  setIsScreeningExpanded,
  preferredQualifyingCriteriaFields,
  appendPreferredQualifyingCriteria,
  removePreferredQualifyingCriteria,
  savePreferredQualifyingCriterias,
}) {
  // hooks
  const { canEdit, mode, clickedStage, setClickedStage } =
    useJobCreationContext();

  // states
  const [isHovered, setIsHovered] = useState('');

  // ref
  const accordianRef = useRef();
  const primarySkillSelectRef = useRef();

  // functions
  const getVisibility = (index, title) => {
    if (isHovered === index) {
      return 'visible';
    }
    return 'hidden';
  };

  return (
    <Accordion
      ref={accordianRef}
      onChange={() => {
        if (clickedStage === 'SCREENING') {
          setClickedStage('AI_INTERVIEW');
        }
      }}
      sx={{
        width: '100%',
      }}
      expanded={isScreeningExpanded}
    >
      <AccordionSummary>
        <Stack spacing="24px" sx={{ width: '100%' }}>
          <Stack spacing="12px">
            <Typography variant="h3">Screening</Typography>
            <Typography
              variant="input"
              color={theme => theme.palette.content.primaryBlack3}
            >
              Define the characteristics of your ideal candidate: E.g., candidate with skills in sales and experience in retail
            </Typography>
          </Stack>
          {!isScreeningExpanded && (
            <Button
              variant={
                mode !== 'EDIT' && clickedStage === 'SCREENING'
                  ? 'contained'
                  : 'soft'
              }
              fullWidth
              onClick={() => setIsScreeningExpanded(!isScreeningExpanded)}
              endIcon={<Iconify icon="tabler:chevron-down" />}
            >
              Custom Setup
            </Button>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack py={3}>
          <Stack spacing="20px">
            <Typography variant="subheading2">Qualifying criteria</Typography>
            <Stack spacing="6px">
              <Typography variant="input" fontWeight={500}>
                Primary skill
              </Typography>
              <FormControl fullWidth>
                <RHFSelect
                  ref={primarySkillSelectRef}
                  labelId="primarySkillLabel"
                  id="primarySkill"
                  name="jobPrimarySkill"
                  disabled={!canEdit}
                >
                  {jobSkills.map(jobSkill => (
                    <MenuItem key={jobSkill} value={jobSkill}>
                      {jobSkill}
                    </MenuItem>
                  ))}
                </RHFSelect>
              </FormControl>
            </Stack>
          </Stack>
          <Stack mt="24px" spacing="16px">
            <Typography variant="subheading2">Preferred criteria</Typography>
            {preferredQualifyingCriteriaFields.map((item, index) => {
              const parentField = `preferredQualifyingCriterias.${index}`;

              const criteriaFieldName = `${parentField}.criteria`;
              const titleFieldName = `${parentField}.title`;
              const title = watch(titleFieldName);

              return (
                <Stack
                  key={item.id}
                  spacing="6px"
                  onMouseEnter={() => setIsHovered(index)}
                  onMouseLeave={() => setIsHovered('')}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ width: '100%' }}
                  >
                    <Typography variant="input" fontWeight={500}>
                      {title || `Criteria ${index + 1}`}
                    </Typography>
                    {canEdit && (
                      <Box
                        sx={{
                          width: '28px',
                          height: '28px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: '4px',
                          padding: '2px',
                          border: theme =>
                            `0.5px solid ${theme.palette.content.primaryBlack3}`,
                          alignSelf: 'flex-end',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease-in-out',
                          visibility: getVisibility(index, title),
                        }}
                        disabled={!canEdit}
                        onClick={() => {
                          if (canEdit) removePreferredQualifyingCriteria(index);
                        }}
                      >
                        <Iconify icon="tabler:trash" />
                      </Box>
                    )}
                  </Stack>
                  <RHFTextField
                    name={criteriaFieldName}
                    disabled={!canEdit || title === 'Experience'}
                    placeholder="Eg. A candidate who has prior experience as a founder"
                  />
                </Stack>
              );
            })}
          </Stack>
          <Button
            startIcon={<Iconify icon="material-symbols:add" />}
            sx={{ width: 'min-content', mt: '20px' }}
            variant="outlined"
            size="small"
            onClick={() =>
              appendPreferredQualifyingCriteria({
                criteria: '',
              })
            }
            disabled={!canEdit}
          >
            Add
          </Button>
        </Stack>
      </AccordionDetails>
      <AccordionActions>
        {isScreeningExpanded && (
          <Button
            fullWidth
            variant="soft"
            onClick={() => setIsScreeningExpanded(!isScreeningExpanded)}
            endIcon={<Iconify icon="tabler:chevron-up" />}
          >
            View less
          </Button>
        )}
      </AccordionActions>
    </Accordion>
  );
}

Screening.propTypes = {
  errors: PropTypes.object,
  watch: PropTypes.func,
  jobSkills: PropTypes.arrayOf(PropTypes.string),
  isScreeningExpanded: PropTypes.bool,
  setIsScreeningExpanded: PropTypes.func,
  preferredQualifyingCriteriaFields: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      criteria: PropTypes.string,
    }),
  ),
  appendPreferredQualifyingCriteria: PropTypes.func,
  removePreferredQualifyingCriteria: PropTypes.func,
  savePreferredQualifyingCriterias: PropTypes.func,
};
